import { Fragment, useState } from 'react';
import { Alert, Button, FormFeedback } from 'reactstrap';
import styled from 'styled-components';
import { FieldArray, Formik, useFormikContext } from 'formik';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import { StickyTd, StickyTh } from '../tables/StickyCell';
import { N_YEARS } from '../../constants/years';
import { timeseriesArray } from '../../utils/utils';
import ScrollingTable from '../tables/ScrollingTable';
import { useUnitsMap } from '../../contexts/UnitContext';
import { useDataTransforms } from '../../utils/hooks';
import DropdownButton from '../basics/DropdownButton';
import {
  SubmitButton,
  TemplateForm,
  ValidatedInput,
} from './template-components';
import { isSpecialRevenueStream } from '../../constants/revenueStreams';
import RevenueUnitsModal from './RevenueUnitsModal';
import RevenueStreamMenu from './RevenueStreamMenu';
import {
  isYearSpecified,
  percentsValidForYear,
} from '../../utils/revenueStreams';
import {
  INVERTED_REVENUE_UNITS_BY_DEFAULT,
  REVENUE_UNITS_BY_DEFAULT,
} from '../../constants/units';
import TableCsvButton from '../tables/TableCsvButton';
import { asCurrencyWithCents, asPercentage } from '../../utils/formatters';
import useCropExportFileName from '../../hooks/useCropExportFileName';

const FormTable = styled(ScrollingTable)`
  border: none;
`;

const MinWidthTh = styled.th`
  min-width: 90px;
`;

const defaultRevenueStream = {
  name: 'New Channel',
  unit: 'lb',
  lbsPerUnit: 1,
  unitsPerTree: null,
  percent: [],
  unitCost: [],
};

const defaultTimberStream = {
  ...defaultRevenueStream,
  unit: 'bft',
  unitsPerTree: [],
  lbsPerUnit: null,
};

const exportCols = [
  { displayString: 'Channel', accessor: 'name' },
  { displayString: 'Start year', accessor: 'startYear' },
  { displayString: 'End year', accessor: 'endYear' },
  {
    displayString: 'Percent of yield',
    accessor: 'percent',
    format: asPercentage,
  },
  {
    displayString: 'Price per unit',
    accessor: 'unitCost',
    format: asCurrencyWithCents,
  },
  { displayString: 'Unit', accessor: 'unit' },
  {
    displayString: 'Conversion',
    accessor: 'conversion',
  },
  {
    displayString: 'Conversion units',
    accessor: 'conversionUnits',
  },
];

const exportData = (
  revenueStreams,
  revenueUnitsMap,
  invertedRevenueUnitsMap,
  yieldUnits,
) => {
  const years = timeseriesArray({ length: N_YEARS }).filter(
    (y) => isYearSpecified(revenueStreams, y) || y === 0,
  );
  return revenueStreams.flatMap((revenueStream) => {
    const {
      lbsPerUnit,
      name,
      percent,
      unit,
      unitCost,
      unitsPerTree,
    } = revenueStream;
    const displayUnit = revenueUnitsMap[unit]?.displayCsv || unit;
    const transformToUnit =
      revenueUnitsMap[unit]?.transformToUnit || _.identity;
    const invertedTransformToUnit =
      invertedRevenueUnitsMap[unit]?.transformToUnit || _.identity;
    return years.map((year, i) => ({
      name,
      startYear: year + 1,
      endYear: i === years.length - 1 ? N_YEARS : years[i + 1],
      percent: percent[year],
      unitCost: invertedTransformToUnit(unitCost[year]),
      unit: displayUnit,
      conversion:
        transformToUnit(unitsPerTree?.[year]) ??
        (displayUnit === yieldUnits.displayCsv
          ? lbsPerUnit // Don't want to display kg/lb or m^3/bft
          : yieldUnits.transformToUnit(lbsPerUnit)),
      conversionUnits: unitsPerTree?.[year]
        ? `${displayUnit} / tree`
        : `${yieldUnits.displayCsv} / unit`,
    }));
  });
};

// Helper to check that there is a value and there are no errors.
// This is used instead of yup because we only want to check years
// that exist.
const isValidNumber = (values, errors, name) =>
  !_.get(errors, name) && typeof _.get(values, name) === 'number';

// Used instead of slice because arrays may not always have 30 values.
const arrayWithValue = (original, key, value) => {
  const newArray = [...original];
  newArray[key] = value;
  // Remove null or undefined values from the end of the list
  while (newArray[newArray.length - 1] == null && newArray.length > 0) {
    newArray.pop();
  }
  return newArray;
};

const TableInput = styled((props) => {
  return (
    <ValidatedInput {...props}>
      {({ renderInput }) => renderInput()}
    </ValidatedInput>
  );
})`
  border: 1.5px solid #fff;
  font-size: 1em;
  padding: 8px;
  margin: 0;
  height: 100%;
  border-radius: 0;
  &:focus {
    box-shadow: none;
    border-color: #fff;
    &.is-invalid {
      box-shadow: none;
    }
  }
`;

const AddYearButton = ({ yearOptions }) => {
  const { values, setValues } = useFormikContext();

  const setOption = ({ key }) => {
    const previousSpecified = timeseriesArray({
      length: key,
      start: 0,
    })
      .reverse()
      .find((y) => isYearSpecified(values.revenueStreams, y));
    setValues({
      ...values,
      revenueStreams: values.revenueStreams.map((s) => {
        // Copy the values from the previous specified year.
        return {
          ...s,
          percent: arrayWithValue(s.percent, key, s.percent[previousSpecified]),
          unitCost: arrayWithValue(
            s.unitCost,
            key,
            s.unitCost[previousSpecified],
          ),
          unitsPerTree:
            s.unitsPerTree &&
            arrayWithValue(
              s.unitsPerTree,
              key,
              s.unitsPerTree[previousSpecified],
            ),
        };
      }),
    });
  };
  return (
    <DropdownButton
      id="add-year"
      maxMenuHeight="150px"
      options={yearOptions}
      setOption={setOption}
      selectedOption="Add year"
    />
  );
};

const AddStreamButton = ({ isTimber, ...props }) => (
  <FieldArray name="revenueStreams">
    {({ push }) => (
      <Button
        size="sm"
        color="link"
        onClick={() =>
          push({
            ...(isTimber ? defaultTimberStream : defaultRevenueStream),
            localId: nanoid(), // Used as a key for React
          })
        }
        {...props}
      >
        ＋ Add sales channel
      </Button>
    )}
  </FieldArray>
);

const StreamHeader = ({
  stream: { id, isDeleted, unitsPerTree },
  streamIdx,
  editUnits,
}) => {
  const { errors } = useFormikContext();
  return (
    !isDeleted && (
      <th colSpan={unitsPerTree ? 3 : 2} className="p-0">
        <div className="d-flex justify-content-between align-items-center">
          <div className="flex-grow-1">
            <TableInput
              name={`revenueStreams[${streamIdx}].name`}
              invalid={!!_.get(errors, `revenueStreams[${streamIdx}].name`)}
            />
          </div>
          <FieldArray name="revenueStreams">
            {({ replace }) => {
              const deleteStream = () =>
                replace(streamIdx, {
                  ...defaultRevenueStream,
                  id,
                  isDeleted: true,
                });
              return (
                <RevenueStreamMenu
                  editUnits={editUnits}
                  deleteStream={deleteStream}
                  id={id}
                />
              );
            }}
          </FieldArray>
        </div>
      </th>
    )
  );
};

const YearRow = ({ year }) => {
  const { errors, values, setValues } = useFormikContext();
  const showRow = isYearSpecified(values.revenueStreams, year) || year === 0;
  const nextSpecified =
    timeseriesArray({
      length: N_YEARS - year,
      start: year + 1,
    }).find((y) => isYearSpecified(values.revenueStreams, y)) || N_YEARS;
  const percentsValid = percentsValidForYear(values.revenueStreams, year);
  const removeYear = () =>
    setValues({
      ...values,
      revenueStreams: values.revenueStreams.map((s) =>
        s.isDeleted
          ? s
          : {
              ...s,
              percent: arrayWithValue(s.percent, year, undefined),
              unitCost: arrayWithValue(s.unitCost, year, undefined),
              unitsPerTree:
                s.unitsPerTree &&
                arrayWithValue(s.unitsPerTree, year, undefined),
            },
      ),
    });

  return (
    showRow && (
      <tr>
        <StickyTd left>{`${year + 1}${
          year + 1 < nextSpecified ? `-${nextSpecified}` : ''
        }`}</StickyTd>
        {values.revenueStreams.map(
          ({ id, unitsPerTree, isDeleted, localId, unit }, streamIdx) => {
            if (isDeleted) {
              return null;
            }
            const unitSpec = REVENUE_UNITS_BY_DEFAULT[unit];
            const invertedUnitSpec = INVERTED_REVENUE_UNITS_BY_DEFAULT[unit];
            return (
              <Fragment key={id || localId}>
                <td className="p-0">
                  <TableInput
                    type="number"
                    parse={parseFloat}
                    name={`revenueStreams[${streamIdx}].percent[${year}]`}
                    min={0}
                    max={100}
                    step="any"
                    invalid={
                      !percentsValid ||
                      !isValidNumber(
                        values,
                        errors,
                        `revenueStreams[${streamIdx}].percent[${year}]`,
                      )
                    }
                    unit="%"
                  />
                </td>
                <td className="p-0">
                  <TableInput
                    type="number"
                    parse={parseFloat}
                    name={`revenueStreams[${streamIdx}].unitCost[${year}]`}
                    min={0}
                    step="any"
                    invalid={
                      !isValidNumber(
                        values,
                        errors,
                        `revenueStreams[${streamIdx}].unitCost[${year}]`,
                      )
                    }
                    unitSpec={invertedUnitSpec}
                    unit={invertedUnitSpec ? undefined : `/${unit}`}
                    currency
                  />
                </td>
                {unitsPerTree && (
                  <td className="p-0">
                    <TableInput
                      type="number"
                      parse={parseFloat}
                      name={`revenueStreams[${streamIdx}].unitsPerTree[${year}]`}
                      min={0}
                      step="any"
                      invalid={
                        !isValidNumber(
                          values,
                          errors,
                          `revenueStreams[${streamIdx}].unitsPerTree[${year}]`,
                        )
                      }
                      unitSpec={unitSpec}
                      unit={unitSpec ? undefined : unit}
                    />
                  </td>
                )}
              </Fragment>
            );
          },
        )}
        <td className="p-0 border-0">
          {year > 0 && (
            <Button color="link" onClick={removeYear}>
              ✕
            </Button>
          )}
        </td>
      </tr>
    )
  );
};

const RevenueTableForm = ({
  data,
  msg,
  loading,
  onSubmit,
  validationSchema,
}) => {
  const [unitEditingIdx, setUnitEditingIdx] = useState(null);
  const [revenueStreams = [], isTimber] = useDataTransforms(data, [
    (d) => d.revenueStreams.filter((s) => !isSpecialRevenueStream(s.name)),
    (d) => d.yield?.unit === 'bft',
  ]);
  const revenueUnitsMap = useUnitsMap(REVENUE_UNITS_BY_DEFAULT);
  const invertedRevenueUnitsMap = useUnitsMap(
    INVERTED_REVENUE_UNITS_BY_DEFAULT,
  );
  const yieldUnits = isTimber ? revenueUnitsMap.bft : revenueUnitsMap.lb;
  const title = useCropExportFileName({
    cropName: data?.name,
    title: 'Revenue Stream',
  });

  if (msg) return <div className="p-3">{msg}</div>;

  return (
    <Formik
      initialValues={{ revenueStreams }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values, errors }) => {
        const yearOptions = timeseriesArray({
          length: N_YEARS,
        })
          .filter((y) => !isYearSpecified(values.revenueStreams, y))
          .map((y) => ({ name: y + 1, key: y }));

        return (
          <>
            {!data.yield?.unit && (
              <Alert className="my-1" color="warning">
                Please configure yield data in Crop Settings to edit revenue
                streams
              </Alert>
            )}
            <TemplateForm className="p-0 overflow-hidden h-100">
              <div className="d-flex flex-column h-100">
                <FormTable>
                  <thead>
                    <tr>
                      <StickyTh left>Channel</StickyTh>
                      {values.revenueStreams.map((stream, streamIdx) => (
                        <StreamHeader
                          key={stream.id || stream.localId}
                          stream={stream}
                          streamIdx={streamIdx}
                          editUnits={() => setUnitEditingIdx(streamIdx)}
                        />
                      ))}
                      <th className="p-0 align-middle">
                        <AddStreamButton
                          aria-label="Add sales channel"
                          disabled={!data.yield?.unit}
                          isTimber={isTimber}
                        />
                      </th>
                    </tr>
                    <tr>
                      <StickyTh left>Year</StickyTh>
                      {values.revenueStreams.map(
                        ({ id, localId, unit, unitsPerTree, isDeleted }) => {
                          const displayUnit =
                            revenueUnitsMap[unit]?.displayUnit || unit;
                          return (
                            !isDeleted && (
                              <Fragment key={id || localId}>
                                <MinWidthTh>% of Yield</MinWidthTh>
                                <MinWidthTh className="border-right">
                                  Price
                                </MinWidthTh>
                                {unitsPerTree && (
                                  <MinWidthTh>{displayUnit} / tree</MinWidthTh>
                                )}
                              </Fragment>
                            )
                          );
                        },
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {timeseriesArray({ length: N_YEARS }).map((year) => (
                      <YearRow key={year} year={year} />
                    ))}
                    {isYearSpecified(values.revenueStreams, 0) &&
                      yearOptions.length > 0 && (
                        <tr>
                          <StickyTd left colSpan={3}>
                            <AddYearButton yearOptions={yearOptions} />
                          </StickyTd>
                        </tr>
                      )}
                  </tbody>
                </FormTable>
                <div className="d-flex align-items-center">
                  {!!revenueStreams.length && (
                    <TableCsvButton
                      className="mr-2"
                      cols={exportCols}
                      data={exportData(
                        revenueStreams,
                        revenueUnitsMap,
                        invertedRevenueUnitsMap,
                        yieldUnits,
                      )}
                      title={title}
                    />
                  )}
                  <SubmitButton
                    disabled={!data.yield?.unit}
                    loading={loading}
                  />
                  {typeof errors.revenueStreams === 'string' && (
                    <FormFeedback className="d-block ml-2">
                      {errors.revenueStreams}
                    </FormFeedback>
                  )}
                </div>
              </div>
            </TemplateForm>
            <FieldArray name="revenueStreams">
              {({ replace }) => (
                <RevenueUnitsModal
                  editing={
                    unitEditingIdx != null &&
                    values.revenueStreams[unitEditingIdx]
                  }
                  // Get the yield unit from the crop data rather than the
                  // revenue stream because it may be overwritten with a
                  // custom value.
                  yieldUnit={data.yield?.unit}
                  onClose={() => setUnitEditingIdx(null)}
                  updateStream={(s) => replace(unitEditingIdx, s)}
                />
              )}
            </FieldArray>
          </>
        );
      }}
    </Formik>
  );
};

export default RevenueTableForm;
